// Tournament Concierge — READ-ONLY player/spectator Q&A. The APP computes the
// live data (this file), the AI only PHRASES it (askConcierge Cloud Function).
// Completely separate from the admin edit-command AI (askAI/processAICommand).
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { pickPremiumCurrentEventId } from '../lib/pickCurrentEvent.js'

let _callable = null
function callable() {
  if (!_callable) _callable = httpsCallable(getFunctions(app, 'us-central1'), 'askConcierge')
  return _callable
}

// Send a question + the app-computed context to the read-only endpoint.
export async function askConcierge(question, category, context, playerName) {
  try {
    const res = await callable()({ question, category, context, playerName: playerName || null })
    return res?.data?.answer || "I don't have that in the live data right now."
  } catch {
    return "I couldn't reach the tournament concierge just now — try again in a moment."
  }
}

export function resolveCurrentEventId(allTournaments) {
  try { return pickPremiumCurrentEventId(allTournaments || {}) } catch { return null }
}

// Sum best-ball gross + holes played for a team.
function teamAgg(team) {
  const scores = team?.scores || {}
  let gross = 0, thru = 0
  for (let h = 1; h <= 18; h++) {
    const bb = Number((scores[h] ?? scores[String(h)])?.bestBall)
    if (Number.isFinite(bb) && bb > 0) { gross += bb; thru++ }
  }
  return { gross, thru }
}

// Live best-ball match-play state between two teams (up/down + thru).
function matchState(fteams, aId, bId, aName, bName) {
  const ta = fteams[aId]?.scores || {}, tb = fteams[bId]?.scores || {}
  let up = 0, thru = 0
  for (let h = 1; h <= 18; h++) {
    const sa = Number((ta[h] ?? ta[String(h)])?.bestBall)
    const sb = Number((tb[h] ?? tb[String(h)])?.bestBall)
    if (Number.isFinite(sa) && sa > 0 && Number.isFinite(sb) && sb > 0) { thru++; if (sa < sb) up++; else if (sb < sa) up-- }
  }
  if (thru === 0) return 'not started'
  if (up === 0) return `all square thru ${thru}`
  return `${up > 0 ? aName : bName} ${Math.abs(up)} up thru ${thru}`
}

// Build the compact live snapshot the AI answers from. `tournament` is the full
// tournaments/{id} node. `opts.myTeamName` marks the asker's team when known.
export function buildConciergeContext(tournament, opts = {}) {
  const info = tournament?.info || {}
  const flightsRaw = tournament?.flights || {}
  const brackets = tournament?.brackets || {}
  const photosRaw = tournament?.photos || {}

  const flights = []
  for (const [fid, f] of Object.entries(flightsRaw)) {
    if (String(fid).startsWith('_')) continue
    const teams = []
    for (const [tid, t] of Object.entries(f?.teams || {})) {
      const { gross, thru } = teamAgg(t)
      const oppId = t?.info?.opponentId
      teams.push({
        name: t?.info?.name || tid,
        players: (t?.info?.players || []).map((p) => (typeof p === 'string' ? p : p?.name)).filter(Boolean),
        gross: gross || null,
        thru,
        startingHole: t?.info?.startingHole ?? null,
        opponent: oppId ? (f.teams?.[oppId]?.info?.name || null) : null,
      })
    }
    teams.sort((a, b) => (a.gross ?? 999) - (b.gross ?? 999))
    flights.push({ flight: f?.info?.name || fid, teams })
  }

  const matches = []
  for (const [fid, br] of Object.entries(brackets)) {
    const fteams = flightsRaw[fid]?.teams || {}
    const nameOf = (id) => (id ? (fteams[id]?.info?.name || null) : null)
    for (const [rk, round] of Object.entries(br?.rounds || {})) {
      for (const [, m] of Object.entries(round?.matches || {})) {
        const a = nameOf(m?.a), b = nameOf(m?.b)
        if (!a && !b) continue
        matches.push({
          flight: flightsRaw[fid]?.info?.name || fid,
          round: round?.label || rk,
          a, b,
          winner: nameOf(m?.winnerId),
          live: m?.winnerId ? `${nameOf(m.winnerId)} won` : (a && b ? matchState(fteams, m.a, m.b, a, b) : 'awaiting opponent'),
        })
      }
    }
  }

  const photos = Object.values(photosRaw).filter(Boolean)
  const byHole = {}
  photos.forEach((p) => { if (p.hole != null) byHole[p.hole] = (byHole[p.hole] || 0) + 1 })
  const photoSummary = {
    total: photos.length,
    byHole,
    recent: photos.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0)).slice(0, 8)
      .map((p) => ({ hole: p.hole ?? null, caption: (p.caption || '').slice(0, 60), by: p.submittedBy || null })),
  }

  const ctx = {
    event: {
      name: String(info.name || '').replace(/\n/g, ' ').trim(),
      date: info.date || null,
      status: info.status || null,
      format: info.format || '4-Ball Best Ball',
      day: info.currentDay ?? null,
      scoringOpen: info.scoringEnabled ?? null,
    },
    flights,
    matches: matches.slice(0, 48),
    photos: photoSummary,
    schedule: tournament?.teeTimes || null,
    rules: { rulesOfPlay: CLUB.rulesOfPlay || [], clubRules: CLUB.clubRules || [], note: 'Standard USGA Four-Ball (best-ball) rules govern play.' },
  }
  if (opts.myTeamName) ctx.you = { team: opts.myTeamName }
  return ctx
}

// The category home + a canned opening question per category.
export const CONCIERGE_CATEGORIES = [
  { key: 'tournament', icon: '🏆', label: 'Tournament', q: "Who's leading right now, and what are the closest matches?" },
  { key: 'players', icon: '👥', label: 'Players', q: 'Help me find a player — their flight, partner, and how they stand.' },
  { key: 'photos', icon: '📸', label: 'Photos', q: 'What photos are on the wall — any by a specific hole or team?' },
  { key: 'live', icon: '📍', label: 'Live Play', q: "What's happening right now? Show me the best match to watch." },
  { key: 'rules', icon: '📖', label: 'Rules', q: 'I have a Four-Ball rules question.' },
  { key: 'mymatch', icon: '🎯', label: 'My Match', q: "What's my match situation — opponent, tee, and where I stand?" },
]
